// components/CreateInvoiceModal.jsx
import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Button,
  VStack,
  HStack,
  NumberInput,
  NumberInputField,
  useToast,
  Heading,
  Text,
  Divider,
  Box,
  IconButton,
  Spinner,
  useClipboard,
  Flex,
  Tag,
  TagLabel,
  Switch,
  Badge,
  Tooltip,
} from "@chakra-ui/react";
import { CheckIcon, CopyIcon, DeleteIcon, WarningIcon } from "@chakra-ui/icons";
import { useNavigate, useLocation } from "react-router-dom";
import { useCognito } from "../utils/useCognito";
import UserPickerDrawer from "./UserPickerDrawer";
import InvoiceProductPickerDrawer from "./InvoiceProductPickerDrawer";
import FacilityPickerDrawer from "./FacilityPickerDrawer";
import OrderPickerDrawer from "./OrderPickerDrawer";
import { useDisclosure } from "@chakra-ui/react";

const HOVER_SCALE = { transform: "scale(1)" };
const API_CREATE =
  "https://w6mxt54h5f.execute-api.us-east-2.amazonaws.com/Prod/api/createInvoice";
const API_GET =
  "https://w6mxt54h5f.execute-api.us-east-2.amazonaws.com/Prod/api/getInvoice";

const emptyLineItem = { label: "", quantity: 1, unitPrice: 0 };
// 👇 default used for *enabled* prompt payment discount
const DEFAULT_DISCOUNT_PERCENTAGE = 40;
// 👇 default days-from-created when creating a new invoice
const DEFAULT_DUE_DAYS = 60;

const usd = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

function toLocalDatetimeInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
    d.getDate()
  )}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function fromLocalDatetimeInput(val) {
  try {
    const d = new Date(val);
    return Number.isNaN(d.getTime()) ? null : d.toISOString();
  } catch {
    return null;
  }
}

// helper: compute days between createdAtLocal (datetime-local string) and dueDateStr ("YYYY-MM-DD")
function computeDueInDays(createdAtLocal, dueDateStr) {
  try {
    const baseISO = fromLocalDatetimeInput(createdAtLocal);
    if (!baseISO || !dueDateStr) return 0;

    const base = new Date(baseISO);
    const [y, m, d] = String(dueDateStr).slice(0, 10).split("-").map(Number);
    if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d))
      return 0;

    const baseOnly = new Date(
      base.getFullYear(),
      base.getMonth(),
      base.getDate()
    );
    const endOnly = new Date(y, m - 1, d);

    const diff = Math.round((endOnly - baseOnly) / 86400000);
    return Number.isFinite(diff) ? diff : 0;
  } catch {
    return 0;
  }
}

// helper: shift createdAtLocal by N days and return "YYYY-MM-DD"
function computeDueDateFromOffset(createdAtLocal, offsetDays) {
  try {
    const baseISO = fromLocalDatetimeInput(createdAtLocal);
    if (!baseISO) return "";
    const base = new Date(baseISO);
    const copy = new Date(base);
    copy.setDate(copy.getDate() + (Number(offsetDays) || 0));
    const p = (n) => String(n).padStart(2, "0");
    return `${copy.getFullYear()}-${p(copy.getMonth() + 1)}-${p(
      copy.getDate()
    )}`;
  } catch {
    return "";
  }
}

function makeNowLocalDatetime() {
  const now = new Date();
  const p = (n) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(
    now.getDate()
  )}T${p(now.getHours())}:${p(now.getMinutes())}`;
}

export default function CreateInvoiceModal({
  isOpen,
  onClose,
  initialData = {}, // can include { invoiceId, invoice }
  onSuccess,
  mode = "create", // "create" | "edit"
}) {
  const isEdit = mode === "edit";
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const { getIdToken } = useCognito();

  const searchParams = new URLSearchParams(location.search);
  const isTestMode = searchParams.get("test") === "true";

  // ✅ prevent re-init loops while typing
  const didInitRef = useRef(false);
  const dueManuallyTouchedRef = useRef(false);

  // success ids
  const [createdId, setCreatedId] = useState(null);
  const [updatedId, setUpdatedId] = useState(null);

  // load state
  const [loadingInvoice, setLoadingInvoice] = useState(false);

  // user selection (human friendly)
  const [userId, setUserId] = useState(initialData.userId || "");
  const [selectedUser, setSelectedUser] = useState(null);
  const [isUserPickerOpen, setIsUserPickerOpen] = useState(false);

  // core invoice fields
  const [orderId, setOrderId] = useState(initialData.orderId || "");
  const [facilityId, setFacilityId] = useState(initialData.facilityId || "");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedFacility, setSelectedFacility] = useState(null);
  const [linkOrderEnabled, setLinkOrderEnabled] = useState(
    Boolean((initialData.orderId || "").trim())
  );
  const [linkFacilityEnabled, setLinkFacilityEnabled] = useState(
    Boolean((initialData.facilityId || "").trim())
  );
  const [notes, setNotes] = useState("");
  const [lineItems, setLineItems] = useState([emptyLineItem]);

  // 🧮 discount controls
  const [discountEnabled, setDiscountEnabled] = useState(true);
  const [discountPercentage, setDiscountPercentage] = useState(
    DEFAULT_DISCOUNT_PERCENTAGE
  );

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [createdAtLocal, setCreatedAtLocal] = useState(() =>
    makeNowLocalDatetime()
  );

  const [dueDate, setDueDate] = useState("");
  const [dueInDays, setDueInDays] = useState(DEFAULT_DUE_DAYS);

  // product picker drawer
  const productPicker = useDisclosure();
  const [activeLineIndex, setActiveLineIndex] = useState(0);
  const [activeProductMeta, setActiveProductMeta] = useState({
    productId: null,
    sizeKey: null,
  });
  const [isOrderPickerOpen, setIsOrderPickerOpen] = useState(false);
  const [isFacilityPickerOpen, setIsFacilityPickerOpen] = useState(false);

  const successId = isEdit ? updatedId : createdId;
  const { hasCopied, onCopy } = useClipboard(successId || "");

  // ---------- hydrate helpers
  const hydrateFromInvoice = useCallback(
    (inv) => {
      if (!inv) return;
      const items = (inv.lineItems || [])
        .filter((li) => !/prompt payment discount/i.test(li.label || ""))
        .map((li) => ({
          label: li.label || "",
          quantity: Number(li.quantity ?? 0),
          unitPrice: Number(li.unitPrice ?? 0),
        }));

      setUserId(inv.userId || "");
      setSelectedUser((prev) => prev || null);

      setOrderId(inv.orderId || "");
      setFacilityId(inv.facilityId || "");
      setLinkOrderEnabled(Boolean((inv.orderId || "").trim()));
      setLinkFacilityEnabled(Boolean((inv.facilityId || "").trim()));

      setNotes("");
      setLineItems(items.length ? items : [emptyLineItem]);

      const invDiscountRaw = inv.discountPercentage;
      const invDiscountNum = Number(
        typeof invDiscountRaw === "object" && invDiscountRaw !== null
          ? invDiscountRaw.N ?? invDiscountRaw
          : invDiscountRaw
      );
      const safePct = Number.isFinite(invDiscountNum) ? invDiscountNum : 0;

      setDiscountEnabled(safePct > 0);
      setDiscountPercentage(
        safePct > 0 ? safePct : DEFAULT_DISCOUNT_PERCENTAGE
      );

      const invCreatedAtLocal = toLocalDatetimeInput(inv.createdAt) || createdAtLocal;
      setCreatedAtLocal(invCreatedAtLocal);

      if (inv.dueDate) {
        const raw = String(inv.dueDate);
        const pure = raw.includes("T") ? raw.slice(0, 10) : raw;
        setDueDate(pure);
        const diff = computeDueInDays(invCreatedAtLocal, pure);
        setDueInDays(Number.isFinite(diff) ? diff : DEFAULT_DUE_DAYS);
      } else {
        setDueDate("");
        setDueInDays(DEFAULT_DUE_DAYS);
      }

      // editing: don't auto-fight due changes
      dueManuallyTouchedRef.current = true;
    },
    [createdAtLocal]
  );

  const fetchInvoiceIfNeeded = useCallback(async () => {
    if (!isEdit || !initialData?.invoiceId) return;
    if (initialData?.invoice) {
      hydrateFromInvoice(initialData.invoice);
      return;
    }

    setLoadingInvoice(true);
    try {
      const token = getIdToken();
      const res = await fetch(API_GET, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ invoiceId: initialData.invoiceId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to load invoice");
      const inv = data.invoices?.[0];
      if (!inv) throw new Error("Invoice not found");
      hydrateFromInvoice(inv);
    } catch (err) {
      console.error("Edit hydrate failed:", err?.message || err);
      toast({
        title: "Couldn’t fetch invoice",
        description:
          "If you opened this from the table, we’ll use the passed data.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoadingInvoice(false);
    }
  }, [
    isEdit,
    initialData?.invoiceId,
    initialData?.invoice,
    hydrateFromInvoice,
    getIdToken,
    toast,
  ]);

  // ✅ init ONLY once per open (prevents createdAt snapping back)
  useEffect(() => {
    if (!isOpen) {
      didInitRef.current = false;
      dueManuallyTouchedRef.current = false;
      return;
    }
    if (didInitRef.current) return;
    didInitRef.current = true;

    setCreatedId(null);
    setUpdatedId(null);

    if (isEdit) {
      if (initialData?.invoice) {
        hydrateFromInvoice(initialData.invoice);
      } else {
        fetchInvoiceIfNeeded();
      }
    } else {
      // fresh create — defaults ONCE per open
      setDiscountEnabled(true);
      setDiscountPercentage(DEFAULT_DISCOUNT_PERCENTAGE);

      const initialDays = DEFAULT_DUE_DAYS;
      setDueInDays(initialDays);

      const nowLocal = makeNowLocalDatetime();
      setCreatedAtLocal(nowLocal);

      const initialDue = computeDueDateFromOffset(nowLocal, initialDays);
      setDueDate(initialDue);

      dueManuallyTouchedRef.current = false;
    }
  }, [
    isOpen,
    isEdit,
    initialData?.invoice,
    hydrateFromInvoice,
    fetchInvoiceIfNeeded,
  ]);

  // ✅ keep dueDate aligned with createdAt + dueInDays (create mode only),
  // but don't fight the user if they manually edited due controls.
  useEffect(() => {
    if (!isOpen) return;
    if (isEdit) return;
    if (dueManuallyTouchedRef.current) return;

    const safeDays = Number.isFinite(Number(dueInDays)) ? Number(dueInDays) : 0;
    const newDue = computeDueDateFromOffset(createdAtLocal, safeDays);
    if (newDue) setDueDate(newDue);
  }, [isOpen, isEdit, createdAtLocal, dueInDays]);

  // ---------- totals (no hidden multipliers)
  const lineTotals = useMemo(
    () =>
      lineItems.map(
        (it) => (Number(it.quantity || 0) * Number(it.unitPrice || 0)) || 0
      ),
    [lineItems]
  );

  const subtotal = useMemo(
    () => lineTotals.reduce((a, b) => a + (Number.isFinite(b) ? b : 0), 0),
    [lineTotals]
  );

  const effectiveDiscountPct = useMemo(() => {
    if (!discountEnabled) return 0;
    const num = Number(discountPercentage || 0);
    return Number.isFinite(num) && num > 0 ? num : 0;
  }, [discountEnabled, discountPercentage]);

  const discountAmount = useMemo(
    () => subtotal * (effectiveDiscountPct / 100),
    [subtotal, effectiveDiscountPct]
  );

  const totalDue = useMemo(
    () => Math.max(0, subtotal - discountAmount),
    [subtotal, discountAmount]
  );

  const submitDiscountPct = () => effectiveDiscountPct;

  // ---------- handlers
  const handleLineItemChange = (i, field, value) => {
    const next = [...lineItems];
    next[i][field] = field === "label" ? value : Number(value);
    setLineItems(next);
  };

  const addLineItem = () => {
    setLineItems((prev) => [...prev, emptyLineItem]);
  };

  const removeLineItem = (i) => {
    const next = [...lineItems];
    next.splice(i, 1);
    setLineItems(next.length ? next : [emptyLineItem]);
  };

  const doSubmit = async (payload, successSetter) => {
    setIsSubmitting(true);
    try {
      const token = getIdToken();
      const res = await fetch(API_CREATE, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Request failed");
      const id = data.invoiceId || payload.invoiceId;
      successSetter(id);
      onSuccess?.({ invoiceId: id });
    } catch (err) {
      toast({ title: "Error", description: err.message, status: "error" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitCreate = () => {
    if (!userId || lineItems.length === 0) {
      toast({
        title: "User and at least one line item are required",
        status: "error",
      });
      return;
    }
    const effectivePct = submitDiscountPct();

    doSubmit(
      {
        userId,
        ...(linkOrderEnabled && orderId ? { orderId } : {}),
        ...(linkFacilityEnabled && facilityId ? { facilityId } : {}),
        dueDate,
        createdAt: fromLocalDatetimeInput(createdAtLocal) || undefined,
        notes,
        lineItems,
        discountPercentage: effectivePct,
      },
      setCreatedId
    );
  };

  const handleSubmitEdit = () => {
    if (!initialData?.invoiceId) {
      toast({
        title: "Edit error",
        description: "Missing invoiceId",
        status: "error",
      });
      return;
    }
    if (!userId || lineItems.length === 0) {
      toast({
        title: "User and at least one line item are required",
        status: "error",
      });
      return;
    }
    const effectivePct = submitDiscountPct();

    doSubmit(
      {
        mode: "edit",
        invoiceId: initialData.invoiceId,
        userId,
        ...(linkOrderEnabled && orderId ? { orderId } : {}),
        ...(linkFacilityEnabled && facilityId ? { facilityId } : {}),
        dueDate,
        createdAt: fromLocalDatetimeInput(createdAtLocal) || undefined,
        notes,
        lineItems,
        discountPercentage: effectivePct,
      },
      setUpdatedId
    );
  };

  const handleUserPicked = (user) => {
    setSelectedUser(user);
    setUserId(user.UserId || "");
    setIsUserPickerOpen(false);
    toast({
      title: "User selected",
      description: `${user.FirstName || ""} ${user.LastName || ""} (${user.email})`,
      status: "success",
      duration: 3000,
      isClosable: true,
    });
  };

  const handleProductPicked = ({ product, sizeKey, label, unitPrice }) => {
    setLineItems((prev) => {
      const next = [...prev];
      const idx = activeLineIndex ?? 0;
      const existing = next[idx] || { ...emptyLineItem };

      const productId = product.id || product.qCode;
      const same =
        existing.productId === productId && existing.sizeKey === sizeKey;

      const quantity = same ? Number(existing.quantity || 0) + 1 : 1;

      next[idx] = {
        ...existing,
        label,
        unitPrice,
        quantity,
        productId,
        sizeKey,
      };

      return next;
    });

    setActiveProductMeta({
      productId: product.id || product.qCode,
      sizeKey,
    });

    productPicker.onClose();
  };

  const openProductPickerForLine = (idx) => {
    const line = lineItems[idx];
    setActiveLineIndex(idx);
    setActiveProductMeta({
      productId: line?.productId || null,
      sizeKey: line?.sizeKey || null,
    });
    productPicker.onOpen();
  };

  // ---------- UI bits
  const renderUserBlock = () => {
    const name =
      selectedUser &&
      `${selectedUser.FirstName || ""} ${selectedUser.LastName || ""}`.trim();
    const email = selectedUser?.email;
    const facilityName =
      selectedUser?.facilityName ||
      selectedUser?.FacilityName ||
      selectedUser?.BAAFacilityName ||
      "";

    const hasRichUser = !!selectedUser;

    return (
      <Box borderWidth="1px" borderRadius="lg" borderColor="gray.700" p={3} bg="gray.100">
        {hasRichUser ? (
          <Flex justify="space-between" align={{ base: "flex-start", md: "center" }} gap={3}>
            <VStack align="flex-start" spacing={0.5}>
              <Text fontSize="sm" fontWeight="semibold">
                {name || "(No name on file)"}
              </Text>
              {email && (
                <Text fontSize="sm" color="gray.400">
                  {email}
                </Text>
              )}
              {facilityName && (
                <Tag size="sm" variant="subtle" colorScheme="teal" mt={1}>
                  <TagLabel>{facilityName}</TagLabel>
                </Tag>
              )}
              {userId && (
                <Text fontSize="xs" color="gray.500" fontFamily="mono" mt={1}>
                  User ID: {userId}
                </Text>
              )}
            </VStack>
            <Button
              variant="outline"
              colorScheme="teal"
              size="sm"
              onClick={() => setIsUserPickerOpen(true)}
              _hover={HOVER_SCALE}
            >
              Change User
            </Button>
          </Flex>
        ) : userId ? (
          <Flex justify="space-between" align="center" gap={3}>
            <Box>
              <Text fontSize="sm" fontWeight="semibold">
                Linked to existing user
              </Text>
              <Text fontSize="xs" color="gray.500" fontFamily="mono" mt={1}>
                User ID: {userId}
              </Text>
              <Text fontSize="xs" color="gray.500" mt={1}>
                You can re-link this invoice to a different user if needed.
              </Text>
            </Box>
            <Button
              variant="outline"
              colorScheme="teal"
              size="sm"
              onClick={() => setIsUserPickerOpen(true)}
              _hover={HOVER_SCALE}
            >
              Change User
            </Button>
          </Flex>
        ) : (
          <Flex justify="space-between" align="center" gap={3}>
            <Box>
              <Text fontSize="sm" fontWeight="semibold">
                No user selected
              </Text>
              <Text fontSize="xs" color="gray.500">
                Pick the account this invoice should belong to.
              </Text>
            </Box>
            <Button
              colorScheme="teal"
              size="sm"
              onClick={() => setIsUserPickerOpen(true)}
              _hover={HOVER_SCALE}
            >
              Pick User
            </Button>
          </Flex>
        )}
      </Box>
    );
  };

  const renderSuccess = () => (
    <VStack spacing={6} align="center" py={6}>
      <Heading size="md">{isEdit ? "Invoice Updated" : "Invoice Created"}</Heading>
      <HStack w="100%" maxW="680px" spacing={2}>
        <Input isReadOnly value={successId || ""} width="100%" fontFamily="monospace" />
        <IconButton
          icon={hasCopied ? <CheckIcon /> : <CopyIcon />}
          onClick={onCopy}
          aria-label="Copy Invoice ID"
          _hover={HOVER_SCALE}
        />
      </HStack>
    </VStack>
  );

  const renderForm = () => (
    <VStack spacing={4} align="stretch">
      {isEdit ? (
        <Heading size="md">Editing: {initialData?.invoiceId || ""}</Heading>
      ) : (
        <Heading size="md">Invoice Details</Heading>
      )}
      <Heading size="sm" color="gray.500">
        Link this invoice to a user and optionally to an order or facility.
      </Heading>

      {renderUserBlock()}

      <VStack align="stretch" spacing={3} mt={2}>
        <FormControl display="flex" alignItems="center" gap={3}>
          <Switch
            isChecked={linkOrderEnabled}
            onChange={(e) => setLinkOrderEnabled(e.target.checked)}
            colorScheme="teal"
          />
          <FormLabel m={0}>Link to Order</FormLabel>
        </FormControl>

        {linkOrderEnabled && (
          <VStack align="stretch" spacing={2}>
            {selectedOrder ? (
              <Box borderWidth="1px" borderRadius="lg" p={3} bg="gray.50">
                <HStack justify="space-between" align="flex-start">
                  <VStack align="flex-start" spacing={0} flex={1}>
                    <Text fontWeight="semibold">Order {selectedOrder.orderId}</Text>
                    {selectedOrder.patientName && (
                      <Text fontSize="sm" color="gray.600">
                        {selectedOrder.patientName}
                      </Text>
                    )}
                    {selectedOrder.facilityName && (
                      <Text fontSize="xs" color="gray.500">
                        {selectedOrder.facilityName}
                      </Text>
                    )}
                    {selectedOrder.status && (
                      <Badge mt={1} colorScheme="purple">
                        {selectedOrder.status}
                      </Badge>
                    )}
                  </VStack>
                  <VStack spacing={2} align="flex-end">
                    <Button
                      size="xs"
                      variant="outline"
                      onClick={() => setIsOrderPickerOpen(true)}
                      _hover={HOVER_SCALE}
                    >
                      Change
                    </Button>
                    <Button
                      size="xs"
                      variant="ghost"
                      colorScheme="red"
                      onClick={() => {
                        setSelectedOrder(null);
                        setOrderId("");
                      }}
                      _hover={HOVER_SCALE}
                    >
                      Clear
                    </Button>
                  </VStack>
                </HStack>
              </Box>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  if (!userId) {
                    toast({
                      title: "Pick a user first",
                      description: "Select a user to filter orders for this invoice.",
                      status: "warning",
                      duration: 3000,
                      isClosable: true,
                    });
                    return;
                  }
                  setIsOrderPickerOpen(true);
                }}
                isDisabled={!userId}
                _hover={HOVER_SCALE}
              >
                {userId ? "Pick Order" : "Pick User First"}
              </Button>
            )}
          </VStack>
        )}

        <FormControl display="flex" alignItems="center" gap={3}>
          <Switch
            isChecked={linkFacilityEnabled}
            onChange={(e) => setLinkFacilityEnabled(e.target.checked)}
            colorScheme="teal"
          />
          <FormLabel m={0}>Link to Facility</FormLabel>
        </FormControl>

        {linkFacilityEnabled && (
          <VStack align="stretch" spacing={2}>
            {selectedFacility ? (
              <Box borderWidth="1px" borderRadius="lg" p={3} bg="gray.50">
                <HStack justify="space-between" align="flex-start">
                  <VStack align="flex-start" spacing={0} flex={1}>
                    <Text fontWeight="semibold">{selectedFacility.name || "Facility"}</Text>
                    {selectedFacility.address && (
                      <Text fontSize="sm" color="gray.600">
                        {selectedFacility.address}
                      </Text>
                    )}
                    <HStack spacing={2} mt={1}>
                      {selectedFacility.npi && (
                        <Badge colorScheme="purple">NPI: {selectedFacility.npi}</Badge>
                      )}
                      {selectedFacility.tin && (
                        <Badge colorScheme="gray">TIN: {selectedFacility.tin}</Badge>
                      )}
                    </HStack>
                  </VStack>
                  <VStack spacing={2} align="flex-end">
                    <Button
                      size="xs"
                      variant="outline"
                      onClick={() => setIsFacilityPickerOpen(true)}
                      _hover={HOVER_SCALE}
                    >
                      Change
                    </Button>
                    <Button
                      size="xs"
                      variant="ghost"
                      colorScheme="red"
                      onClick={() => {
                        setSelectedFacility(null);
                        setFacilityId("");
                      }}
                      _hover={HOVER_SCALE}
                    >
                      Clear
                    </Button>
                  </VStack>
                </HStack>
              </Box>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  if (!userId) {
                    toast({
                      title: "Pick a user first",
                      description: "Select a user to load facilities for this invoice.",
                      status: "warning",
                      duration: 3000,
                      isClosable: true,
                    });
                    return;
                  }
                  setIsFacilityPickerOpen(true);
                }}
                isDisabled={!userId}
                _hover={HOVER_SCALE}
              >
                {userId ? "Pick Facility" : "Pick User First"}
              </Button>
            )}
          </VStack>
        )}
      </VStack>

      <FormControl>
        <FormLabel>Created At</FormLabel>
        <Input
          type="datetime-local"
          value={createdAtLocal}
          onChange={(e) => {
            // user changed base date; in create mode we may auto-adjust dueDate unless user manually touched due
            setCreatedAtLocal(e.target.value);
          }}
        />
      </FormControl>

      <FormControl>
        <FormLabel>Due Date</FormLabel>
        <HStack align="center" spacing={2}>
          <Input
            type="date"
            value={dueDate}
            onChange={(e) => {
              dueManuallyTouchedRef.current = true;
              const val = e.target.value;
              setDueDate(val);
              // 🡒 update days-from-created based on new date
              const diff = computeDueInDays(createdAtLocal, val);
              setDueInDays(diff);
            }}
          />
          <HStack spacing={1}>
            <Text color="gray.600">+</Text>
            <NumberInput
              size="sm"
              min={0}
              max={3650}
              value={dueInDays}
              onChange={(valueStr, valueNum) => {
                dueManuallyTouchedRef.current = true;
                // Chakra gives both string & number
                const n = Number.isFinite(valueNum) ? valueNum : parseInt(valueStr, 10);
                const safe = Number.isFinite(n) ? n : 0;
                setDueInDays(safe);
                // 🡒 recompute dueDate from days offset
                const newDue = computeDueDateFromOffset(createdAtLocal, safe);
                setDueDate(newDue);
              }}
              w="90px"
            >
              <NumberInputField />
            </NumberInput>
            <Text color="gray.600">days</Text>
          </HStack>
        </HStack>
      </FormControl>

      {/* Prompt Payment Discount Controls */}
      <Box mt={2} p={3} borderWidth="1px" borderRadius="lg" bg="gray.50">
        <HStack justify="space-between" align="center">
          <Box>
            <Text fontSize="sm" fontWeight="semibold">
              Prompt Payment Discount
            </Text>
            <Text fontSize="xs" color="gray.600">
              When enabled, a discount line will appear on the invoice.
            </Text>
          </Box>
          <Switch
            isChecked={discountEnabled}
            onChange={(e) => setDiscountEnabled(e.target.checked)}
            colorScheme="teal"
          />
        </HStack>

        {discountEnabled && (
          <HStack mt={3} spacing={2} align="center">
            <Text fontSize="sm" color="gray.700">
              Discount Percentage
            </Text>
            <NumberInput
              min={0}
              max={100}
              step={1}
              value={discountPercentage}
              onChange={(valueString) => setDiscountPercentage(valueString)}
              w="100px"
              size="sm"
            >
              <NumberInputField />
            </NumberInput>
            <Text>%</Text>
            {Number(discountPercentage) !== DEFAULT_DISCOUNT_PERCENTAGE && (
              <Tooltip label="40 is the standard percentage" hasArrow>
                <WarningIcon color="yellow.500" boxSize={4} />
              </Tooltip>
            )}
          </HStack>
        )}
      </Box>

      <FormControl>
        <FormLabel>Notes</FormLabel>
        <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} />
      </FormControl>

      <Heading size="sm" mt={4}>
        Line Items
      </Heading>

      {lineItems.map((item, idx) => {
        const lineTotal =
          (Number(item.quantity || 0) * Number(item.unitPrice || 0)) || 0;
        return (
          <Flex
            key={idx}
            gap={2}
            align="flex-start"
            direction={{ base: "column", md: "row" }}
          >
            <Input
              placeholder="Description"
              value={item.label}
              onChange={(e) => handleLineItemChange(idx, "label", e.target.value)}
              flex="2"
            />

            <NumberInput
              min={0}
              value={item.quantity}
              onChange={(v) => handleLineItemChange(idx, "quantity", v)}
              flex={{ base: "1", md: "0.6" }}
            >
              <NumberInputField placeholder="Qty" />
            </NumberInput>

            <NumberInput
              min={0}
              value={item.unitPrice}
              onChange={(v) => handleLineItemChange(idx, "unitPrice", v)}
              flex={{ base: "1", md: "0.8" }}
            >
              <NumberInputField placeholder="Unit Price" />
            </NumberInput>

            <Box
              minW={{ base: "100%", md: "120px" }}
              textAlign={{ base: "left", md: "right" }}
              fontWeight="bold"
              pt={{ base: 1, md: 2 }}
            >
              {usd.format(lineTotal)}
            </Box>

            <VStack spacing={1} align={{ base: "stretch", md: "flex-end" }} flexShrink={0}>
              <Button
                size="xs"
                variant="outline"
                colorScheme="teal"
                onClick={() => openProductPickerForLine(idx)}
                _hover={HOVER_SCALE}
              >
                Pick Product
              </Button>

              <Button
                size="xs"
                variant="ghost"
                colorScheme="red"
                leftIcon={<DeleteIcon />}
                onClick={() => removeLineItem(idx)}
                _hover={HOVER_SCALE}
              >
                Remove
              </Button>
            </VStack>
          </Flex>
        );
      })}

      {discountEnabled && effectiveDiscountPct > 0 && (
        <HStack spacing={2} align="center" opacity={0.85}>
          <Input isReadOnly value="Prompt Payment Discount (auto-applied)" />
          <NumberInput isReadOnly value={0}>
            <NumberInputField />
          </NumberInput>
          <NumberInput isReadOnly value={0}>
            <NumberInputField />
          </NumberInput>
          <Text minW="120px" fontWeight="bold" color="green.600" textAlign="right">
            -{usd.format(discountAmount)}
          </Text>
          <Box w="92px" />
        </HStack>
      )}

      <Button
        onClick={addLineItem}
        size="sm"
        colorScheme="blue"
        alignSelf="flex-start"
        _hover={HOVER_SCALE}
      >
        Add Line Item
      </Button>

      <Divider my={4} />

      <Box textAlign="right">
        <Text fontWeight="bold">Subtotal: {usd.format(subtotal)}</Text>

        {discountEnabled && effectiveDiscountPct > 0 && (
          <Text color="green.600">
            Discount ({effectiveDiscountPct}%): -{usd.format(discountAmount)}
          </Text>
        )}

        <Text fontSize="xl" fontWeight="bold">
          {isEdit ? "Total (after update):" : "Total Due:"} {usd.format(totalDue)}
        </Text>
      </Box>
    </VStack>
  );

  const gotoHighlighted = (id) => {
    const qs = `?highlight=${id}${isTestMode ? "&test=true" : ""}`;
    navigate({ search: qs });
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="4xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {isEdit
              ? updatedId
                ? `Invoice ${updatedId} Updated`
                : `Edit Invoice`
              : createdId
              ? `Invoice ${createdId} Created`
              : "Create Invoice"}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {isEdit
              ? loadingInvoice
                ? <Spinner />
                : updatedId
                ? renderSuccess()
                : renderForm()
              : createdId
              ? renderSuccess()
              : renderForm()}
          </ModalBody>
          <ModalFooter>
            {isEdit ? (
              updatedId ? (
                <HStack spacing={4}>
                  <Button onClick={onClose} _hover={HOVER_SCALE}>
                    Close
                  </Button>
                  <Button
                    colorScheme="teal"
                    onClick={() => gotoHighlighted(updatedId)}
                    _hover={HOVER_SCALE}
                  >
                    View Invoice
                  </Button>
                </HStack>
              ) : (
                <>
                  <Button onClick={onClose} mr={3} _hover={HOVER_SCALE}>
                    Cancel
                  </Button>
                  <Button
                    colorScheme="teal"
                    onClick={handleSubmitEdit}
                    isLoading={isSubmitting}
                    _hover={HOVER_SCALE}
                  >
                    Save Changes
                  </Button>
                </>
              )
            ) : createdId ? (
              <HStack spacing={4}>
                <Button onClick={onClose} _hover={HOVER_SCALE}>
                  Close
                </Button>
                <Button
                  colorScheme="teal"
                  onClick={() => gotoHighlighted(createdId)}
                  _hover={HOVER_SCALE}
                >
                  View Invoice
                </Button>
              </HStack>
            ) : (
              <>
                <Button onClick={onClose} mr={3} _hover={HOVER_SCALE}>
                  Cancel
                </Button>
                <Button
                  colorScheme="teal"
                  onClick={handleSubmitCreate}
                  isLoading={isSubmitting}
                  _hover={HOVER_SCALE}
                >
                  Create Invoice
                </Button>
              </>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>

      <UserPickerDrawer
        isOpen={isUserPickerOpen}
        onClose={() => setIsUserPickerOpen(false)}
        onSelectUser={handleUserPicked}
      />

      <InvoiceProductPickerDrawer
        isOpen={productPicker.isOpen}
        onClose={productPicker.onClose}
        onPick={handleProductPicked}
        selectedProductId={activeProductMeta.productId}
        selectedSizeKey={activeProductMeta.sizeKey}
      />

      <OrderPickerDrawer
        isOpen={isOrderPickerOpen}
        onClose={() => setIsOrderPickerOpen(false)}
        userId={userId}
        isTestMode={isTestMode}
        onSelectOrder={(o) => {
          setOrderId(o.orderId);
          setSelectedOrder(o);
        }}
      />

      <FacilityPickerDrawer
        isOpen={isFacilityPickerOpen}
        onClose={() => setIsFacilityPickerOpen(false)}
        userId={userId}
        onSelectFacility={(f) => {
          setFacilityId(f.id || "");
          setSelectedFacility(f);
        }}
      />
    </>
  );
}
